import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, CheckCircle, FileText, TrendingUp, Shield, Volume2, VolumeX, Pause, Play, Download, MessageCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../zustand/ThemeStore';
import { useUserStore } from '../zustand/UserStore';
import CallInfoBanner from '../components/analysis/CallInfoBanner';
import AudioPlayer from '../components/analysis/AudioPlayer';
import Tabs from '../components/analysis/Tabs';
import StrengthsOpportunities from '../components/analysis/StrengthsOpportunities';
import Recommendations from '../components/analysis/Recommendations';
import DetailedAnalysis from '../components/analysis/DetailedAnalysis';
import TranscriptBubble from '../components/analysis/TranscriptBubble';
import AnalysisLoader from '../components/analysis/AnalysisLoader';
import CallChatInterface from '../components/analysis/CallChatInterface';
import { getApiBaseUrl } from '../utils/config';

interface Strength {
  line: string;
  comment: string;
}

interface Opportunity {
  line: string;
  comment: string;
}

interface KeyOfImprovement {
  area: string;
  what_went_wrong: string;
  evidence: string;
  sales_coaching_technique: string;
}

interface Dimension {
  category: string;
  score: number | string;
  justification: string;
  highlights: string[];
  improvements: string[];
  coaching: string[];
  affirmations?: string[];
  keep_doing?: string[];
}

interface CallSummary {
  overall_average: number;
  dimensions: Dimension[];
  overall_strengths: Strength[];
  overall_opportunities: Opportunity[];
  keys_of_improvement: KeyOfImprovement[];
  recommendations: string[];
  kudos?: string[];
}

interface Analysis {
  call_summary: CallSummary;
}

interface CallData {
  id: string;
  representative: string;
  customer_name: string;
  timestamp: string;
  status: string;
  transcript: string;
  analysis: Analysis;
  audio_url?: string;
  call_duration?: string;
  overall_score?: number;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export default function CallAnalysisPage() {
  const isDarkMode = useTheme((set)=>set.theme)
  const [activeTab, setActiveTab] = useState('summary');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [callData, setCallData] = useState<CallData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Chat messages state - persists during session
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  
  // Speech synthesis state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const speechSynthRef = useRef<SpeechSynthesisUtterance | null>(null);
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const currentUser = useUserStore((state) => state.user);

  // Keep these variables to prevent unused warnings
  console.log('Analysis state:', { currentUser });
  
  // Initialize chat messages with welcome message if empty
  useEffect(() => {
    if (chatMessages.length === 0 && callData) {
      setChatMessages([{
        role: 'assistant',
        content: "Hi! I'm your AI assistant for this call analysis. Ask me anything about the call - specific moments, scores, suggestions, or patterns I noticed. What would you like to know?",
        timestamp: new Date()
      }]);
    }
  }, [callData, chatMessages.length]);

  const extractDuration = (): string => {
    // First try to use stored call_duration, fallback to extracting from transcript
    if (callData?.call_duration) {
      return callData.call_duration;
    }
    
    if (!callData?.transcript) return "0:00";
    const timeMatches = callData.transcript.match(/\[(\d{2}:\d{2})\]/g);
    if (timeMatches && timeMatches.length > 0) {
      const lastTime = timeMatches[timeMatches.length - 1].match(/\[(\d{2}:\d{2})\]/);
      return lastTime ? lastTime[1] : "7:00";
    }
    return "7:00";
  };

  // Generate readable analysis text for speech synthesis
  const generateAnalysisText = useCallback((): string => {
    if (!callData?.analysis?.call_summary) return '';
    
    const summary = callData.analysis.call_summary;
    const parts: string[] = [];
    
    // Overall score
    parts.push(`Call Analysis Summary for ${callData.customer_name} with representative ${callData.representative}.`);
    parts.push(`Overall performance score: ${summary.overall_average} out of 10.`);
    
    // Top strengths (limit to 3)
    if (summary.overall_strengths?.length > 0) {
      parts.push('Key strengths identified:');
      summary.overall_strengths.slice(0, 3).forEach((s, i) => {
        parts.push(`${i + 1}. ${s.comment}`);
      });
    }
    
    // Top opportunities (limit to 3)
    if (summary.overall_opportunities?.length > 0) {
      parts.push('Areas for improvement:');
      summary.overall_opportunities.slice(0, 3).forEach((o, i) => {
        parts.push(`${i + 1}. ${o.comment}`);
      });
    }
    
    // Detailed analysis by dimension
    if (summary.dimensions?.length > 0) {
      parts.push('Detailed Analysis by Category:');
      
      summary.dimensions.forEach((dim) => {
        const score = typeof dim.score === 'number' ? dim.score : dim.score;
        parts.push(`${dim.category}: Score ${score} out of 10.`);
        
        if (dim.justification) {
          parts.push(`Assessment: ${dim.justification}`);
        }
        
        // Highlights (limit to 2 per dimension)
        if (dim.highlights?.length > 0) {
          parts.push('Highlights:');
          dim.highlights.slice(0, 2).forEach((h, i) => {
            parts.push(`${i + 1}. ${h}`);
          });
        }
        
        // Improvements (limit to 2 per dimension)
        if (dim.improvements?.length > 0) {
          parts.push('Areas to improve:');
          dim.improvements.slice(0, 2).forEach((imp, i) => {
            parts.push(`${i + 1}. ${imp}`);
          });
        }
        
        // Coaching tips (limit to 1 per dimension)
        if (dim.coaching?.length > 0) {
          parts.push(`Coaching tip: ${dim.coaching[0]}`);
        }
        
        // Keep doing / Affirmations (limit to 1)
        if (dim.keep_doing && dim.keep_doing.length > 0) {
          parts.push(`Keep doing: ${dim.keep_doing[0]}`);
        } else if (dim.affirmations && dim.affirmations.length > 0) {
          parts.push(`Affirmation: ${dim.affirmations[0]}`);
        }
      });
    }
    
    // Top recommendations (limit to 3)
    if (summary.recommendations?.length > 0) {
      parts.push('Key Recommendations:');
      summary.recommendations.slice(0, 3).forEach((r, i) => {
        parts.push(`${i + 1}. ${r}`);
      });
    }
    
    // Keys of improvement
    if (summary.keys_of_improvement?.length > 0) {
      parts.push('Critical Areas for Improvement:');
      summary.keys_of_improvement.slice(0, 2).forEach((key, i) => {
        parts.push(`${i + 1}. ${key.area}: ${key.what_went_wrong}`);
        if (key.sales_coaching_technique) {
          parts.push(`Coaching technique: ${key.sales_coaching_technique}`);
        }
      });
    }
    
    // Kudos if available
    if (summary.kudos && summary.kudos.length > 0) {
      parts.push('Recognition and Kudos:');
      summary.kudos.slice(0, 2).forEach((kudo) => {
        parts.push(kudo);
      });
    }
    
    parts.push('End of detailed analysis summary.');
    
    return parts.join(' ');
  }, [callData]);

  // Speech synthesis controls
  const handleSpeak = useCallback(() => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech is not supported in your browser.');
      return;
    }
    
    // If already speaking, stop
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      return;
    }
    
    const text = generateAnalysisText();
    if (!text) {
      alert('No analysis data available to read.');
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Try to find a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Daniel')
    ) || voices.find(v => v.lang.startsWith('en'));
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };
    
    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };
    
    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isSpeaking, generateAnalysisText]);

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, [isPaused]);

  // Download analysis script as text file
  const handleDownloadScript = useCallback(() => {
    const text = generateAnalysisText();
    if (!text) {
      alert('No analysis data available to download.');
      return;
    }
    
    const header = `Call Analysis Script\n${'='.repeat(50)}\n`;
    const callInfo = `Customer: ${callData?.customer_name}\nRepresentative: ${callData?.representative}\nDate: ${callData?.timestamp ? new Date(callData.timestamp).toLocaleString() : 'N/A'}\nOverall Score: ${callData?.analysis?.call_summary?.overall_average || 'N/A'}/10\n\n${'='.repeat(50)}\n\n`;
    
    const fullContent = header + callInfo + text;
    
    const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${callData?.customer_name || 'analysis'}_script_${callData?.id || 'unknown'}.txt`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [generateAnalysisText, callData]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  // Fetch call analysis data
  useEffect(() => {
    const fetchCallData = async () => {
      if (!id) {
        setError('Call ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        
        // Get call details with authorization handled by backend
        const response = await axios.get(`${getApiBaseUrl()}/api/v1/calls/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const apiData = response.data.call;
        
        // Transform API response to match component structure
        // Handle both old and new API response formats
        const isNewFormat = apiData.data.call_summary && typeof apiData.data.call_summary === 'object';
        
        const transformedData: CallData = {
          id: apiData.id,
          representative: apiData.representative,
          customer_name: apiData.customer_name,
          timestamp: apiData.timestamp,
          status: 'success',
          transcript: apiData.transcript,
          audio_url: apiData.audio_url,
          analysis: {
            call_summary: {
              overall_average: isNewFormat 
                ? apiData.data.call_summary.overall_average 
                : (apiData.data.overall_average || apiData.data.total_score),
              dimensions: isNewFormat 
                ? apiData.data.call_summary.dimensions 
                : (apiData.data.dimensions || [
                  { 
                    category: 'Opening & Control', 
                    score: apiData.data.opening_control || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Trust & Rapport', 
                    score: apiData.data.trust_rapport || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Motivation Discovery (P-P-F)', 
                    score: apiData.data.motivation_discovery || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Deal-Killer ID & Resolution', 
                    score: apiData.data.deal_killer_resolution || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Questioning & Listening Quality', 
                    score: apiData.data.questioning_listening || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Offer & Value Framing', 
                    score: apiData.data.offer_value_framing || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Negotiation & Objection Handling', 
                    score: apiData.data.negotiation_objection || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Closing & Commitment', 
                    score: apiData.data.closing_commitment || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Emotional Control & Non-Neediness', 
                    score: apiData.data.emotional_control || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  },
                  { 
                    category: 'Overall Structure & Flow', 
                    score: apiData.data.structure_flow || 0, 
                    justification: 'Based on call analysis',
                    highlights: [],
                    improvements: [],
                    coaching: []
                  }
                ]),
              overall_strengths: isNewFormat 
                ? apiData.data.call_summary.overall_strengths 
                : (apiData.data.overall_strengths || []),
              overall_opportunities: isNewFormat 
                ? apiData.data.call_summary.overall_opportunities 
                : (apiData.data.overall_opportunities || []),
              keys_of_improvement: isNewFormat 
                ? apiData.data.call_summary.keys_of_improvement 
                : (apiData.data.keys_of_improvement || []),
              recommendations: isNewFormat 
                ? apiData.data.call_summary.recommendations 
                : (apiData.data.recommendations || []),
              kudos: isNewFormat 
                ? apiData.data.call_summary.kudos 
                : undefined
            }
          }
        };
        
        setCallData(transformedData);
      } catch (error: any) {
        if (error.response?.status === 403) {
          setAccessDenied(true);
        } else {
          console.error('Error fetching call data:', error);
          setError('Failed to load call data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCallData();
  }, [id]);

  const tabs = [
    { id: 'summary', label: 'Summary & Scores', icon: TrendingUp },
    { id: 'detailed', label: 'Detailed Analysis', icon: CheckCircle },
    { id: 'transcript', label: 'Transcript', icon: FileText },
    { id: 'chat', label: 'AI Q&A', icon: MessageCircle }
  ];

  const getScoreColor = (score: number | string): string => {
    if (score === "N/A") return '#9CA3AF';
    const numScore = typeof score === 'number' ? score : parseFloat(score);
    if (numScore >= 8) return '#1a9f6b';
    if (numScore >= 6) return '#fbbf24';
    return '#ef4444';
  };

  const getScoreBadge = (score: number | string): string => {
    if (score === "N/A") return 'N/A';
    const numScore = typeof score === 'number' ? score : parseFloat(score);
    if (numScore >= 8) return 'Excellent';
    if (numScore >= 6) return 'Good';
    return 'Needs Work';
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to handle bar click and navigate to detailed analysis
  const handleBarClick = (data: any) => {
    if (!data || data.index === undefined) return;
    
    const index = data.index;
    
    // Switch to detailed analysis tab
    setActiveTab('detailed');
    
    // Wait for tab to render, then scroll to the section
    setTimeout(() => {
      const element = document.getElementById(`dimension-${index}`);
      if (element) {
        // Scroll to element with offset for header
        const yOffset = -100; 
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({ top: y, behavior: 'smooth' });
        
        // Add highlight effect
        element.classList.add('scale-105', 'ring-4', isDarkMode ? 'ring-blue-500' : 'ring-blue-400');
        element.style.transition = 'all 0.5s ease-in-out';
        
        // Remove highlight after 2 seconds
        setTimeout(() => {
          element.classList.remove('scale-105', 'ring-4', isDarkMode ? 'ring-blue-500' : 'ring-blue-400');
        }, 2000);
      }
    }, 100);
  };

  // Function to handle audio file download
  const handleDownloadAudio = async () => {
    try {
      const audioUrl = callData?.audio_url;
      
      if (!audioUrl) {
        alert('Audio file not available for download');
        return;
      }
      
      console.log('Starting download from URL:', audioUrl);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `${callData?.customer_name || 'customer'}_call_${callData?.id || 'unknown'}.mp3`;
      link.target = '_blank';
      
      // For cross-origin downloads, we might need to fetch and create blob
      try {
        const audioResponse = await fetch(audioUrl);
        const blob = await audioResponse.blob();
        const blobUrl = URL.createObjectURL(blob);
        link.href = blobUrl;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the blob URL after download
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        console.log('Download initiated successfully');
      } catch (fetchError) {
        console.log('Blob download failed, trying direct download:', fetchError);
        // Fallback: try direct download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (error) {
      console.error('Error downloading audio file:', error);
      alert('Failed to download audio file. Please try again.');
    }
  };

  // Function to handle transcript download
  const handleDownloadTranscript = () => {
    try {
      if (!callData?.transcript) {
        alert('Transcript not available for download');
        return;
      }

      // Create formatted transcript content
      const header = `Call Transcript\n`;
      const separator = `${'='.repeat(50)}\n`;
      const callInfo = `Customer: ${callData.customer_name}\nRepresentative: ${callData.representative}\nDate: ${new Date(callData.timestamp).toLocaleString()}\nCall ID: ${callData.id}\n`;
      const transcriptHeader = `\n${'='.repeat(50)}\nTRANSCRIPT\n${'='.repeat(50)}\n\n`;
      const transcriptContent = callData.transcript;
      
      const fullContent = header + separator + callInfo + transcriptHeader + transcriptContent;

      // Create blob with UTF-8 encoding
      const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${callData.customer_name}_transcript_${callData.id}.txt`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      console.log('Transcript download initiated successfully');
    } catch (error) {
      console.error('Error downloading transcript:', error);
      alert('Failed to download transcript. Please try again.');
    }
  };

  if (loading) {
    return (
      <AnalysisLoader/>
    );
  }

  if (accessDenied) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'} flex justify-center items-center`}>
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
            You don't have permission to view this call analysis.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'} flex justify-center items-center`}>
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>Error</h3>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>{error}</p>
        </div>
      </div>
    );
  }

  if (!callData || !callData.analysis || !callData.analysis.call_summary) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'} flex justify-center items-center`}>
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>Call not found</h3>
          <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>Unable to load call analysis data.</p>
        </div>
      </div>
    );
  }

  const summary = callData.analysis.call_summary;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}> 
      <CallInfoBanner
        representative={callData.representative}
        customer_name={callData.customer_name}
        timestamp={callData.timestamp}
        duration={extractDuration()}
        overall_average={summary?.overall_average || 'N/A'}
        isDarkMode={isDarkMode}
        getScoreColor={getScoreColor}
      >
        {/* Speech & Download Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={handleSpeak}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all font-medium text-xs shadow-sm ${
              isSpeaking
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-[#1a9f6b] hover:bg-[#148856] text-white'
            }`}
            title={isSpeaking ? 'Stop speaking' : 'Listen to analysis summary'}
          >
            {isSpeaking ? (
              <>
                <VolumeX className="w-3.5 h-3.5" />
                Stop
              </>
            ) : (
              <>
                <Volume2 className="w-3.5 h-3.5" />
                Read
              </>
            )}
          </button>
          
          {isSpeaking && (
            <button
              onClick={handlePauseResume}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all text-xs shadow-sm ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'
                  : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
              }`}
              title={isPaused ? 'Resume' : 'Pause'}
            >
              {isPaused ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          
          <button
            onClick={handleDownloadScript}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all font-medium text-xs shadow-sm ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
            title="Download analysis script as text file"
          >
            <Download className="w-3.5 h-3.5" />
            Script
          </button>
        </div>
      </CallInfoBanner>
      
      <AudioPlayer
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        currentTime={currentTime}
        formatTime={formatTime}
        duration={extractDuration()}
        isDarkMode={isDarkMode}
        audioSrc={callData?.audio_url}
        onTimeUpdate={setCurrentTime}
        onDownload={handleDownloadAudio}
        showDownload={!!callData?.audio_url}
      />
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isDarkMode={isDarkMode}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'summary' && (
          <div className="space-y-6">
            <StrengthsOpportunities
              strengths={summary?.overall_strengths || []}
              opportunities={summary?.overall_opportunities || []}
              isDarkMode={isDarkMode}
            />

            {/* Kudos Section - only show if kudos exist */}
            {summary?.kudos && summary.kudos.length > 0 && (
              <div className={`rounded-xl p-6 shadow-lg ${
                isDarkMode 
                  ? 'bg-[#0a1414] border border-[#1a2424]' 
                  : 'bg-white'
              }`}>
                <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  🎉 Kudos & Recognition
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {summary.kudos.map((kudo, index) => (
                    <div key={index} className={`rounded-lg p-4 border-l-4 border-green-500 ${
                      isDarkMode 
                        ? 'bg-green-900/20 border-green-400' 
                        : 'bg-green-50 border-green-500'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white text-sm font-bold rounded-full flex items-center justify-center">
                          ✓
                        </div>
                        <p className={`text-sm ${
                          isDarkMode ? 'text-green-100' : 'text-green-800'
                        }`}>
                          {kudo}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Performance Score Bar Chart */}
            <div className={`rounded-xl p-6 shadow-lg ${
              isDarkMode 
                ? 'bg-[#0a1414] border border-[#1a2424]' 
                : 'bg-white'
            }`}>
              <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                📊 Performance Scores by Category
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={summary?.dimensions?.map((dim, idx) => ({
                      category: dim.category.replace(' & ', '\n& '),
                      score: typeof dim.score === 'number' ? dim.score : dim.score === 'N/A' ? 0 : parseFloat(dim.score),
                      displayScore: dim.score,
                      index: idx
                    })) || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                    <XAxis 
                      dataKey="category" 
                      stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                      fontSize={12}
                      angle={-45}
                      textAnchor="end"
                      height={120}
                    />
                    <YAxis 
                      stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                      domain={[0, 10]}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        borderRadius: '8px',
                        color: isDarkMode ? '#FFFFFF' : '#111827'
                      }}
                      formatter={(value, _) => [
                        typeof value === 'number' && value === 0 ? 'N/A' : value,
                        'Score'
                      ]}
                    />
                    <Bar 
                      dataKey="score" 
                      fill="#1a9f6b"
                      radius={[4, 4, 0, 0]}
                      onClick={handleBarClick}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* <div className="grid grid-cols-1 gap-6">
              <ScoreBreakdown
                dimensions={summary?.dimensions || []}
                isDarkMode={isDarkMode}
                getScoreColor={getScoreColor}
                getScoreBadge={getScoreBadge}
              />
            </div> */}

            {/* Strengths vs Opportunities Chart
            <div className={`rounded-xl p-6 shadow-lg ${
              isDarkMode 
                ? 'bg-[#0a1414] border border-[#1a2424]' 
                : 'bg-white'
            }`}>
              <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                ⚡ Key Areas Analysis
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        category: 'Strengths',
                        count: summary?.overall_strengths?.length || 0,
                        color: '#1a9f6b'
                      },
                      {
                        category: 'Opportunities',
                        count: summary?.overall_opportunities?.length || 0,
                        color: '#fbbf24'
                      },
                      {
                        category: 'Key Improvements',
                        count: summary?.keys_of_improvement?.length || 0,
                        color: '#f97316'
                      },
                      {
                        category: 'Recommendations',
                        count: summary?.recommendations?.length || 0,
                        color: '#3b82f6'
                      }
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
                    <XAxis 
                      dataKey="category" 
                      stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                    />
                    <YAxis 
                      stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#1F2937' : '#FFFFFF',
                        border: `1px solid ${isDarkMode ? '#374151' : '#E5E7EB'}`,
                        borderRadius: '8px',
                        color: isDarkMode ? '#FFFFFF' : '#111827'
                      }}
                      formatter={(value, _) => [value, 'Count']}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#1a9f6b"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div> */}

            {/* Keys of Improvement Section */}
            {/* {summary?.keys_of_improvement && summary.keys_of_improvement.length > 0 && (
              <div className={`rounded-xl p-6 shadow-lg ${
                isDarkMode 
                  ? 'bg-[#0a1414] border border-[#1a2424]' 
                  : 'bg-white'
              }`}>
                <h3 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                  🎯 Key Areas for Improvement
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {summary.keys_of_improvement.map((item, index) => (
                    <div key={index} className={`rounded-lg p-5 border-l-4 border-orange-500 ${
                      isDarkMode 
                        ? 'bg-orange-900/10 border-[#1a2424]' 
                        : 'bg-orange-50 border-orange-200'
                    }`}>
                      <div className="flex items-start gap-3 mb-4">
                        <span className="flex-shrink-0 w-7 h-7 bg-orange-500 text-white text-sm font-bold rounded-full flex items-center justify-center">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <h4 className={`font-semibold text-base mb-1 ${
                            isDarkMode ? 'text-orange-300' : 'text-orange-700'
                          }`}>
                            {item.area}
                          </h4>
                        </div>
                      </div>
                      
                      <div className="space-y-4 ml-10">
                        <div>
                          <h5 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-red-300' : 'text-red-600'
                          }`}>
                            What went wrong:
                          </h5>
                          <p className={`text-sm leading-relaxed ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {item.what_went_wrong}
                          </p>
                        </div>

                        <div>
                          <h5 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-blue-300' : 'text-blue-600'
                          }`}>
                            Evidence:
                          </h5>
                          <p className={`text-sm font-mono rounded-md p-2 ${
                            isDarkMode ? 'text-gray-400 bg-gray-800/50' : 'text-gray-600 bg-gray-100'
                          }`}>
                            {item.evidence}
                          </p>
                        </div>

                        <div>
                          <h5 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                            isDarkMode ? 'text-green-300' : 'text-green-600'
                          }`}>
                            Coaching Technique:
                          </h5>
                          <p className={`text-sm leading-relaxed font-medium ${
                            isDarkMode ? 'text-green-200' : 'text-green-700'
                          }`}>
                            {item.sales_coaching_technique}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            <Recommendations
              recommendations={summary?.recommendations || []}
              dimensions={summary?.dimensions || []}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
        {activeTab === 'detailed' && (
          <DetailedAnalysis
            dimensions={summary?.dimensions || []}
            isDarkMode={isDarkMode}
            getScoreColor={getScoreColor}
            getScoreBadge={getScoreBadge}
          />
        )}
        {activeTab === 'chat' && callData && (
          <div className="h-[600px]">
            <CallChatInterface 
              callId={callData.id}
              isDarkMode={isDarkMode}
              isOpen={true}
              messages={chatMessages}
              setMessages={setChatMessages}
            />
          </div>
        )}
        {activeTab === 'transcript' && (
          <div className={`rounded-xl p-6 shadow-lg ${
            isDarkMode 
              ? 'bg-[#0a1414] border border-[#1a2424]' 
              : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-6">
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                📝 Call Transcript
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={() => navigator.clipboard.writeText(callData.transcript)}
                  className="px-4 py-2 bg-[#1a9f6b] hover:bg-[#148856] text-white text-sm rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Transcript
                </button>
                <button
                  onClick={handleDownloadTranscript}
                  className={`px-4 py-2 text-white text-sm rounded-lg transition-colors flex items-center gap-2 ${
                    isDarkMode 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Transcript
                </button>
              </div>
            </div>
            <div className={`rounded-lg p-6 max-h-[800px] overflow-y-auto ${
              isDarkMode ? 'bg-[#050a0a]' : 'bg-gray-50'
            }`}>
              <div className="space-y-4">
                {callData.transcript.split('\n').filter(line => line.trim()).map((line, index) => {
                  const isRep = line.includes('Representative:');
                  const timeMatch = line.match(/\[(\d{2}:\d{2})\]/);
                  const timestamp = timeMatch ? timeMatch[1] : '';
                  const content = line.replace(/\[\d{2}:\d{2}\]\s*(Representative:|Customer:)\s*/, '');
                  return (
                    <TranscriptBubble
                      key={index}
                      isRep={isRep}
                      timestamp={timestamp}
                      content={content}
                      isDarkMode={isDarkMode}
                      repName={callData.representative}
                      customerName={callData.customer_name}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}